import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, LayoutGrid, Boxes, AlertTriangle, PiggyBank, Tag, Pencil, Trash2 } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "primary", testid, to }) => {
  const inner = (
    <Card className={`p-5 h-full ${to ? "cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="mt-1 font-heading text-3xl font-bold">{value}</div>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tone === "danger" ? "bg-red-100 text-red-700" : tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
  return to ? <Link to={to} data-testid={`${testid}-link`} className="block">{inner}</Link> : inner;
};

export default function Dashboard() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [data, setData] = useState({ products: 0, machines: 0, low: 0, empty: 0, notes: 0 });
  const [deals, setDeals] = useState({ total_savings: 0, total_units: 0, count: 0, recent: [], top_products: [] });
  const [editP, setEditP] = useState(null); // purchase being edited
  const [editForm, setEditForm] = useState({ cases: 1, paid: "", baseline: "" });

  const loadDeals = useCallback(async () => {
    try {
      const { data: d } = await api.get("/purchases/summary");
      if (d) setDeals(d);
    } catch (err) { console.error("Failed to load deal savings:", err); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [prods, machines, inv, notes, dealSum] = await Promise.all([
          api.get("/products"), api.get("/machines"), api.get("/inventory/overview"),
          Promise.resolve({ data: [] }), api.get("/purchases/summary").catch(() => ({ data: null })),
        ]);
        setData({
          products: prods.data.length, machines: machines.data.length,
          low: inv.data.stats.low, empty: inv.data.stats.empty,
          notes: notes.data.filter((n) => !n.read).length,
        });
        if (dealSum.data) setDeals(dealSum.data);
      } catch (err) {
        console.error("Failed to load dashboard overview:", err);
      }
    })();
  }, []);

  const openEdit = (r) => {
    setEditP(r);
    setEditForm({ cases: r.cases, paid: String(r.paid_case_price), baseline: String(r.baseline_case_price) });
  };
  const editSavings = () => {
    const cs = Number(editForm.cases) || 0;
    const paid = Number(editForm.paid);
    const base = Number(editForm.baseline);
    if (isNaN(paid) || isNaN(base)) return 0;
    return (base - paid) * cs;
  };
  const saveEdit = async () => {
    const cs = Number(editForm.cases) || 0;
    const paid = Number(editForm.paid);
    const base = Number(editForm.baseline);
    if (cs <= 0) return toast.error("Enter number of cases");
    if (isNaN(paid) || paid < 0 || isNaN(base) || base < 0) return toast.error("Enter valid prices");
    try {
      await api.put(`/purchases/${editP.id}`, {
        product_id: editP.product_id, product_name: editP.product_name, size: editP.size || "",
        cases: cs, case_pack: editP.case_pack || 0,
        baseline_case_price: base, paid_case_price: paid,
      });
      toast.success("Purchase updated");
      setEditP(null);
      loadDeals();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const deletePurchase = async (r) => {
    if (!window.confirm(`Delete this ${r.product_name} purchase?`)) return;
    try {
      await api.delete(`/purchases/${r.id}`);
      toast.success("Purchase deleted");
      loadDeals();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const quick = [
    { to: "/admin/planner/machines", label: "View machines", icon: LayoutGrid, roles: ["admin", "manager", "stocker"] },
    { to: "/admin/planner/fill-planner", label: "Plan a fill", icon: Package, roles: ["admin", "manager"] },
    { to: "/admin/planner/route", label: "Stocking route", icon: Boxes, roles: ["admin", "manager", "stocker"] },
    { to: "/admin/planner/shopping", label: "Shopping lists", icon: Boxes, roles: ["admin", "manager", "stocker"] },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">Stocking overview</h1>
        <p className="text-sm text-muted-foreground">Here’s the state of your operation.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat icon={Package} label="Products" value={data.products} testid="stat-products" to="/admin/planner/products" />
        <Stat icon={LayoutGrid} label="Machines" value={data.machines} testid="stat-machines" to="/admin/planner/machines" />
        <Stat icon={AlertTriangle} label="Low slots" value={data.low} tone="amber" testid="stat-low" to="/admin/planner/inventory" />
        <Stat icon={Boxes} label="Empty slots" value={data.empty} tone="danger" testid="stat-empty" to="/admin/planner/inventory" />
        <Stat icon={PiggyBank} label="Deal savings" value={`$${Number(deals.total_savings || 0).toFixed(2)}`} tone="primary" testid="stat-savings" to="/admin/planner/shopping" />
      </div>

      <h2 className="mt-8 mb-3 font-heading text-lg font-semibold">Quick actions</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quick.filter((q) => q.roles.includes(user?.role)).map((q) => {
          const Icon = q.icon;
          return (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.to.slice(1)}`}>
              <Card className="p-5 hover:-translate-y-0.5 transition-transform hover:shadow-md cursor-pointer h-full">
                <Icon className="h-6 w-6 text-primary" />
                <div className="mt-3 font-semibold">{q.label}</div>
              </Card>
            </Link>
          );
        })}
      </div>

      {deals.count > 0 && (
        <>
          <div className="mt-8 mb-3 flex items-center gap-2">
            <h2 className="font-heading text-lg font-semibold">Deal savings</h2>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary" data-testid="deals-total">
              ${Number(deals.total_savings || 0).toFixed(2)} extra profit · {deals.total_units} units · {deals.count} buy{deals.count !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4" data-testid="deals-recent-card">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent on-sale purchases</div>
              <div className="space-y-2">
                {deals.recent.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-sm" data-testid={`deal-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{r.product_name}{r.size ? <span className="ml-1 text-xs text-muted-foreground">· {r.size}</span> : null}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {r.cases} case{r.cases !== 1 ? "s" : ""} @ ${Number(r.paid_case_price).toFixed(2)} (reg ${Number(r.baseline_case_price).toFixed(2)}) · {r.purchased_at ? new Date(r.purchased_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="font-mono font-bold text-green-700">+${Number(r.savings).toFixed(2)}</span>
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" data-testid={`deal-edit-${r.id}`} onClick={() => openEdit(r)} aria-label="Edit purchase"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`deal-delete-${r.id}`} onClick={() => deletePurchase(r)} aria-label="Delete purchase"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            {deals.top_products.length > 0 && (
              <Card className="p-4" data-testid="deals-top-card">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top saving products</div>
                <div className="space-y-2">
                  {deals.top_products.map((t) => (
                    <div key={t.product_name} className="flex items-center justify-between gap-3 rounded-lg border p-2.5 text-sm">
                      <div className="flex min-w-0 items-center gap-2">
                        <Tag className="h-4 w-4 shrink-0 text-primary" />
                        <span className="truncate font-medium">{t.product_name}</span>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-mono font-bold text-green-700">+${Number(t.savings).toFixed(2)}</div>
                        <div className="text-[11px] text-muted-foreground">{t.count} buy{t.count !== 1 ? "s" : ""} · {t.units} units</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </>
      )}

      <Dialog open={!!editP} onOpenChange={(o) => !o && setEditP(null)}>
        <DialogContent data-testid="deal-edit-dialog">
          <DialogHeader><DialogTitle>Edit purchase</DialogTitle></DialogHeader>
          {editP && (
            <div className="space-y-3">
              <div className="rounded-lg border p-2.5 text-sm">
                <div className="font-medium">{editP.product_name}{editP.size ? <span className="ml-1 text-xs text-muted-foreground">· {editP.size}</span> : null}</div>
                {editP.case_pack ? <div className="text-[11px] text-muted-foreground">{editP.case_pack} per case</div> : null}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Cases</Label><Input data-testid="deal-edit-cases" type="number" min="1" value={editForm.cases} onChange={(e) => setEditForm({ ...editForm, cases: e.target.value })} /></div>
                <div><Label>Paid / case ($)</Label><Input data-testid="deal-edit-paid" type="number" step="0.01" value={editForm.paid} onChange={(e) => setEditForm({ ...editForm, paid: e.target.value })} /></div>
                <div><Label>Regular / case ($)</Label><Input data-testid="deal-edit-baseline" type="number" step="0.01" value={editForm.baseline} onChange={(e) => setEditForm({ ...editForm, baseline: e.target.value })} /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2.5 text-sm" data-testid="deal-edit-savings">
                <span className="text-muted-foreground">Extra profit from this deal</span>
                <span className={`font-mono font-bold ${editSavings() > 0 ? "text-green-700" : editSavings() < 0 ? "text-destructive" : ""}`}>${editSavings().toFixed(2)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditP(null)} data-testid="deal-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} data-testid="deal-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
